import { Application, Assets, Container, Graphics, Sprite, Spritesheet } from "pixi.js";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { CORE_ATLAS_JSON } from "./assets";
import { createCombatFlights } from "./board/combatFlights";
import {
  AUTOMATIC_SLIDE_MS,
  BOARD_PIXELS,
  BOARD_SIZE,
  COLORS,
  FALL_DROP_MS,
  FALL_SETTLE_MS,
  FLASH_MS,
  SNAP_MS,
  TILE_ASSET_IDS,
  TILE_SIZE,
} from "./board/constants";
import { createFrictionEffects } from "./board/frictionEffects";
import { createMatchEffects } from "./board/matchEffects";
import { BoardAnimationGeneration } from "./boardAnimation";
import {
  DEFAULT_EFFECT_SETTINGS,
  effectIntensityScale,
  type EffectChannel,
  type EffectSettings,
} from "./effects";
import type { Cascade, TileTransformation } from "./types";
import { wouldSlideMatch } from "./wasm";

interface Props {
  board: number[];
  boardHash: string;
  revision: number;
  cascades: Cascade[];
  transformations: TileTransformation[];
  disabled: boolean;
  queued: boolean;
  colorBlind: boolean;
  effectSettings: EffectSettings;
  fallDelayMs: number;
  lockedRow: number;
  foggedCells: number[];
  wrapMatches: boolean;
  woodOreUnion: boolean;
  onSlide: (
    axis: "row" | "col",
    index: number,
    distance: number,
    automated?: boolean,
  ) => Promise<void>;
}

export interface BoardCanvasHandle {
  playAutomatedSlide: (
    axis: "row" | "col",
    index: number,
    distance: number,
    boardHash: string,
  ) => Promise<boolean>;
}

interface TileView {
  container: Container;
  tile: number;
  baseX: number;
  baseY: number;
}

interface DragState {
  id: number;
  boardHash: string;
  startX: number;
  startY: number;
  axis: "row" | "col" | null;
  index: number;
  offset: number;
  lastX: number;
  lastY: number;
  lastMoveAt: number;
  lastSparkAt: number;
  views: TileView[];
  clones: Container[];
}

interface DeferredAuthority {
  board: number[];
  boardHash: string;
  cascades: Cascade[];
  transformations: TileTransformation[];
}

export const BoardCanvas = forwardRef<BoardCanvasHandle, Props>(function BoardCanvas(
  {
    board,
    boardHash,
    revision,
    cascades,
    transformations,
    disabled,
    queued,
    colorBlind,
    effectSettings = DEFAULT_EFFECT_SETTINGS,
    fallDelayMs,
    lockedRow,
    foggedCells,
    wrapMatches,
    woodOreUnion,
    onSlide,
  },
  ref,
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef(onSlide);
  const boardRef = useRef(board);
  const boardHashRef = useRef(boardHash);
  const disabledRef = useRef(disabled);
  const colorBlindRef = useRef(colorBlind);
  const effectSettingsRef = useRef(effectSettings);
  const fallDelayRef = useRef(fallDelayMs);
  const lockedRowRef = useRef(lockedRow);
  const foggedCellsRef = useRef(foggedCells);
  const matchRulesRef = useRef({ wrapMatches, woodOreUnion });
  const renderRef = useRef<((next: number[]) => void) | null>(null);
  const redrawLockRef = useRef<(() => void) | null>(null);
  const redrawFogRef = useRef<(() => void) | null>(null);
  const cascadeRef = useRef<
    | ((
        steps: Cascade[],
        finalBoard: number[],
        initialTransformations: TileTransformation[],
      ) => void)
    | null
  >(null);
  const draggingRef = useRef(false);
  const deferredAuthorityRef = useRef<DeferredAuthority | null>(null);
  const flushAuthorityRef = useRef<(() => void) | null>(null);
  const automatedSlideRef = useRef<BoardCanvasHandle["playAutomatedSlide"] | null>(null);
  const lastAnimatedHashRef = useRef(boardHash);
  slideRef.current = onSlide;
  boardRef.current = board;
  boardHashRef.current = boardHash;
  disabledRef.current = disabled;
  colorBlindRef.current = colorBlind;
  effectSettingsRef.current = effectSettings;
  fallDelayRef.current = fallDelayMs;
  lockedRowRef.current = lockedRow;
  foggedCellsRef.current = foggedCells;
  matchRulesRef.current = { wrapMatches, woodOreUnion };
  useImperativeHandle(
    ref,
    () => ({
      playAutomatedSlide: (axis, index, distance, expectedHash) =>
        automatedSlideRef.current?.(axis, index, distance, expectedHash) ?? Promise.resolve(false),
    }),
    [],
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let disposed = false;
    let initialized = false;
    let animating = false;
    const animationGeneration = new BoardAnimationGeneration();
    let drag: DragState | null = null;
    let tileViews: TileView[] = [];
    let displayedBoard = [...boardRef.current];

    let automatedAnimationResolve: ((completed: boolean) => void) | null = null;
    const frameIDs = new Set<number>();
    const timeoutIDs = new Set<number>();
    const app = new Application();
    let coreSheet: Spritesheet | null = null;
    const boardLayer = new Container();
    const lockLayer = new Graphics();
    const fogLayer = new Graphics();
    const motionLayer = new Container();
    const effectLayer = new Container();
    const flightLayer = document.createElement("div");
    flightLayer.className = "match-flight-layer";
    flightLayer.setAttribute("aria-hidden", "true");
    document.body.appendChild(flightLayer);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const domAnimations = new Set<Animation>();
    const currentEffectScale = (channel: EffectChannel = "particles") => {
      const setting = effectSettingsRef.current[channel];
      return setting.enabled ? effectIntensityScale(setting.intensity) : 0;
    };

    const trackDOMAnimation = (animation: Animation) => {
      domAnimations.add(animation);
      void animation.finished.catch(() => undefined).finally(() => domAnimations.delete(animation));
      return animation;
    };

    const nextFrame = (callback: FrameRequestCallback) => {
      const id = window.requestAnimationFrame((time) => {
        frameIDs.delete(id);
        if (disposed) return;
        callback(time);
      });
      frameIDs.add(id);
      return id;
    };

    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        const id = window.setTimeout(() => {
          timeoutIDs.delete(id);
          resolve();
        }, ms);
        timeoutIDs.add(id);
      });

    const schedule = (callback: () => void, ms: number) => {
      const id = window.setTimeout(() => {
        timeoutIDs.delete(id);
        if (!disposed) callback();
      }, ms);
      timeoutIDs.add(id);
    };

    const animationActive = (currentGeneration: number) =>
      !disposed && animationGeneration.isCurrent(currentGeneration);

    const { clearMotionEffects, emitFrictionSparks } = createFrictionEffects({
      motionLayer,
      nextFrame,
      currentEffectScale,
      reducedMotion,
      host,
      isDisposed: () => disposed,
    });

    const { emitCombatFlights, playBoardRecoil } = createCombatFlights({
      currentEffectScale,
      reducedMotion,
      isDisposed: () => disposed,
      host,
      flightLayer,
      trackDOMAnimation,
    });

    const tileSprite = (tile: number, tier: 1 | 2): Sprite | null => {
      const id = TILE_ASSET_IDS[tile];
      const texture = id ? coreSheet?.textures[`tile-${id}-t${tier}`] : null;
      return texture ? new Sprite(texture) : null;
    };

    const createTile = (tile: number): Container => {
      const container = new Container();
      const base = COLORS[tile] ?? "#777";
      const shape = new Graphics();
      shape.rect(0, 0, TILE_SIZE, TILE_SIZE).fill({ color: "#15111a" });
      shape.rect(1, 1, TILE_SIZE - 2, TILE_SIZE - 2).fill({ color: "#4a424c" });
      shape.rect(2, 2, TILE_SIZE - 4, 3).fill({ color: "#6a6069" });
      shape.rect(2, 5, 3, TILE_SIZE - 8).fill({ color: "#5a505a" });
      shape.rect(3, TILE_SIZE - 5, TILE_SIZE - 6, 3).fill({ color: "#29242d" });
      shape.rect(TILE_SIZE - 5, 4, 3, TILE_SIZE - 8).fill({ color: "#302a34" });
      shape.rect(6, 6, TILE_SIZE - 12, TILE_SIZE - 12).fill({ color: "#26212b" });
      shape
        .rect(6, 6, TILE_SIZE - 12, TILE_SIZE - 12)
        .stroke({ color: base, width: 1, alpha: 0.32 });
      shape.rect(3, 3, 4, 1).fill({ color: "#8a7b7e" });
      shape.rect(25, 28, 4, 1).fill({ color: "#1c1820" });
      container.addChild(shape);
      const icon = tileSprite(tile, 1);
      if (icon) container.addChild(icon);
      if (colorBlindRef.current) {
        const badgeIDs = ["triangle", "circle", "square", "diamond", "cross", "plus"];
        const texture = coreSheet?.textures[`badge-${badgeIDs[tile]}`];
        if (texture) {
          const badge = new Sprite(texture);
          badge.x = 16;
          badge.y = 16;
          container.addChild(badge);
        }
      }
      return container;
    };

    const clearDragClones = () => {
      if (!drag) return;
      for (const clone of drag.clones) {
        boardLayer.removeChild(clone);
        clone.destroy({ children: true });
      }
      drag.clones = [];
    };

    const replaceBoard = (next: number[]) => {
      clearDragClones();
      drag = null;
      boardLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
      tileViews = next.map((tile, index) => {
        const container = createTile(tile);
        const view = {
          container,
          tile,
          baseX: (index % BOARD_SIZE) * TILE_SIZE,
          baseY: Math.floor(index / BOARD_SIZE) * TILE_SIZE,
        };
        container.x = view.baseX;
        container.y = view.baseY;
        boardLayer.addChild(container);
        return view;
      });
      displayedBoard = [...next];
    };

    const renderBoard = (next: number[]) => {
      animationGeneration.invalidate();
      replaceBoard(next);
    };

    const renderLock = () => {
      lockLayer.clear();
      const row = lockedRowRef.current;
      if (row < 0 || row >= BOARD_SIZE) return;
      const y = row * TILE_SIZE;
      lockLayer
        .rect(1, y + 1, BOARD_PIXELS - 2, TILE_SIZE - 2)
        .fill({ color: "#a66f4e", alpha: 0.12 });
      lockLayer
        .rect(1, y + 1, BOARD_PIXELS - 2, TILE_SIZE - 2)
        .stroke({ color: "#c88e67", width: 2, alpha: 0.9 });
      for (let x = 8; x < BOARD_PIXELS; x += 16) {
        lockLayer
          .moveTo(x, y + 3)
          .lineTo(x + 8, y + 11)
          .stroke({ color: "#c88e67", width: 1, alpha: 0.5 });
      }
    };

    const renderFog = () => {
      fogLayer.clear();
      for (const cell of foggedCellsRef.current) {
        if (cell < 0 || cell >= BOARD_SIZE * BOARD_SIZE) continue;
        const x = (cell % BOARD_SIZE) * TILE_SIZE;
        const y = Math.floor(cell / BOARD_SIZE) * TILE_SIZE;
        fogLayer
          .rect(x + 2, y + 2, TILE_SIZE - 4, TILE_SIZE - 4)
          .fill({ color: "#171c1c", alpha: 0.88 });
        fogLayer.rect(x + 6, y + 7, TILE_SIZE - 12, 4).fill({ color: "#68716c", alpha: 0.45 });
        fogLayer.rect(x + 10, y + 17, TILE_SIZE - 15, 3).fill({ color: "#4b5652", alpha: 0.55 });
      }
    };

    const renderAuthoritative = (next: number[]) => {
      animationGeneration.invalidate();
      delete host.dataset.effect;
      delete host.dataset.dragging;
      delete host.dataset.dragOffset;
      clearMotionEffects();
      effectLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
      replaceBoard(next);
      animating = false;
    };

    const prepareDragLine = (state: DragState) => {
      state.views = tileViews.filter((_, index) =>
        state.axis === "row"
          ? Math.floor(index / BOARD_SIZE) === state.index
          : index % BOARD_SIZE === state.index,
      );
      for (const view of state.views) {
        const before = createTile(view.tile);
        const after = createTile(view.tile);
        boardLayer.addChild(before, after);
        state.clones.push(before, after);
      }
    };

    const applyDragOffset = (state: DragState, offset: number) => {
      state.offset = offset;
      host.dataset.dragOffset = offset.toFixed(2);
      state.views.forEach((view, index) => {
        const wrapped = mod(
          (state.axis === "row" ? view.baseX : view.baseY) + offset,
          BOARD_PIXELS,
        );
        if (state.axis === "row") {
          view.container.x = wrapped;
          const before = state.clones[index * 2];
          const after = state.clones[index * 2 + 1];
          before.x = wrapped - BOARD_PIXELS;
          before.y = view.baseY;
          after.x = wrapped + BOARD_PIXELS;
          after.y = view.baseY;
        } else {
          view.container.y = wrapped;
          const before = state.clones[index * 2];
          const after = state.clones[index * 2 + 1];
          before.x = view.baseX;
          before.y = wrapped - BOARD_PIXELS;
          after.x = view.baseX;
          after.y = wrapped + BOARD_PIXELS;
        }
      });
    };

    const animateOffset = (
      state: DragState,
      target: number,
      done: () => void,
      durationMs = SNAP_MS,
    ) => {
      const start = state.offset;
      const started = performance.now();
      const duration = reducedMotion ? 30 : durationMs;
      const step = (now: number) => {
        if (disposed || drag !== state) return;
        const progress = Math.min(1, (now - started) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        applyDragOffset(state, start + (target - start) * eased);
        if (progress < 1) nextFrame(step);
        else done();
      };
      nextFrame(step);
    };

    automatedSlideRef.current = async (axis, index, distance, expectedHash) => {
      if (
        disposed ||
        !initialized ||
        animating ||
        drag ||
        expectedHash !== boardHashRef.current ||
        index < 0 ||
        index >= BOARD_SIZE ||
        distance === 0 ||
        Math.abs(distance) >= BOARD_SIZE
      )
        return false;
      const sourceBoard = [...displayedBoard];
      const state: DragState = {
        id: -1,
        boardHash: expectedHash,
        startX: 0,
        startY: 0,
        axis,
        index,
        offset: 0,
        lastX: 0,
        lastY: 0,
        lastMoveAt: performance.now(),
        lastSparkAt: 0,
        views: [],
        clones: [],
      };
      drag = state;
      draggingRef.current = true;
      animating = true;
      host.dataset.automaticSlide = axis;
      host.dataset.automaticSlideIndex = String(index);
      host.dataset.automaticSlideDistance = String(distance);
      prepareDragLine(state);
      applyDragOffset(state, 0);
      const submittedHash = boardHashRef.current;
      const request = slideRef.current(axis, index, distance, true).then(
        () => true,
        () => false,
      );
      const animated = await new Promise<boolean>((resolve) => {
        automatedAnimationResolve = resolve;
        animateOffset(
          state,
          distance * TILE_SIZE,
          () => {
            automatedAnimationResolve = null;
            resolve(true);
          },
          AUTOMATIC_SLIDE_MS,
        );
      });
      if (!animated || disposed) {
        await request;
        return false;
      }
      renderBoard(slideBoard(sourceBoard, axis, index, distance));
      host.dataset.automaticSlideCount = String(
        Number(host.dataset.automaticSlideCount ?? "0") + 1,
      );
      delete host.dataset.automaticSlide;
      delete host.dataset.automaticSlideIndex;
      delete host.dataset.automaticSlideDistance;
      delete host.dataset.dragOffset;
      draggingRef.current = false;
      flushAuthorityRef.current?.();
      const submitted = await request;
      if (disposed) return submitted;
      schedule(() => {
        if (boardHashRef.current === submittedHash) {
          renderAuthoritative(boardRef.current);
          animating = false;
        }
      }, 100);
      return submitted;
    };

    const { playLandingImpact, flashMatches } = createMatchEffects({
      currentEffectScale,
      reducedMotion,
      animationActive,
      motionLayer,
      effectLayer,
      host,
      nextFrame,
      effectSettingsRef,
      playBoardRecoil,
      tileSprite,
    });

    const animateTransformations = (changes: TileTransformation[], currentGeneration: number) =>
      new Promise<void>((resolve) => {
        const active = changes.filter(
          ({ index, from, to }) =>
            index >= 0 &&
            index < BOARD_SIZE * BOARD_SIZE &&
            from !== to &&
            displayedBoard[index] !== to,
        );
        if (active.length === 0 || !animationActive(currentGeneration)) {
          resolve();
          return;
        }
        const transformationLayer = new Container();
        const effectScale = currentEffectScale("particles");
        const transformed: TileView[] = [];
        const pulses: Graphics[] = [];
        const fragments: Array<{ view: Graphics; x: number; y: number; vx: number; vy: number }> =
          [];
        effectLayer.addChild(transformationLayer);
        for (const change of active) {
          const index = change.index;
          const previous = tileViews[index];
          if (previous) {
            if (previous.container.parent === boardLayer)
              boardLayer.removeChild(previous.container);
            previous.container.destroy({ children: true });
          }
          const view: TileView = {
            container: createTile(change.to),
            tile: change.to,
            baseX: (index % BOARD_SIZE) * TILE_SIZE,
            baseY: Math.floor(index / BOARD_SIZE) * TILE_SIZE,
          };
          const initialAlpha = reducedMotion ? 0.7 : 0.65 + effectScale * 0.2;
          const initialScale = 0.9;
          view.container.alpha = initialAlpha;
          view.container.scale.set(initialScale);
          view.container.x = view.baseX + ((1 - view.container.scale.x) * TILE_SIZE) / 2;
          view.container.y = view.baseY + ((1 - view.container.scale.y) * TILE_SIZE) / 2;
          boardLayer.addChild(view.container);
          tileViews[index] = view;
          displayedBoard[index] = change.to;
          transformed.push(view);

          const color = COLORS[change.to] ?? "#d5c27b";
          if (effectScale > 0) {
            const pulse = new Graphics();
            pulse
              .rect(view.baseX + 2, view.baseY + 2, TILE_SIZE - 4, TILE_SIZE - 4)
              .fill({ color, alpha: 0.16 + effectScale * 0.28 })
              .stroke({ color: "#ffffff", width: 1, alpha: 0.35 + effectScale * 0.65 });
            pulses.push(pulse);
            transformationLayer.addChild(pulse);
          }
          if (!reducedMotion && effectScale > 0) {
            const fragmentCount = Math.max(1, Math.round(9 * effectScale));
            for (let piece = 0; piece < fragmentCount; piece++) {
              const fragment = new Graphics();
              fragment
                .rect(-2, -2, piece % 2 === 0 ? 4 : 3, piece % 3 === 0 ? 4 : 3)
                .fill({ color });
              const angle = (piece / fragmentCount) * Math.PI * 2 + (index % 4) * 0.18;
              const particle = {
                view: fragment,
                x: view.baseX + TILE_SIZE / 2,
                y: view.baseY + TILE_SIZE / 2,
                vx: Math.cos(angle) * (10 + (piece % 3) * 3),
                vy: Math.sin(angle) * (10 + (piece % 3) * 3),
              };
              fragment.x = particle.x;
              fragment.y = particle.y;
              fragments.push(particle);
              transformationLayer.addChild(fragment);
            }
          }
        }
        host.dataset.transformationCount = String(
          Number(host.dataset.transformationCount ?? "0") + active.length,
        );
        const started = performance.now();
        const duration = reducedMotion ? 70 : 220;
        let completed = false;
        const finish = () => {
          if (completed) return;
          completed = true;
          for (const view of transformed) {
            if (!view.container.destroyed) {
              view.container.alpha = 1;
              view.container.scale.set(1);
              view.container.x = view.baseX;
              view.container.y = view.baseY;
            }
          }
          if (!transformationLayer.destroyed) {
            if (transformationLayer.parent === effectLayer)
              effectLayer.removeChild(transformationLayer);
            transformationLayer.destroy({ children: true });
          }
          resolve();
        };
        const step = (now: number) => {
          if (!animationActive(currentGeneration)) {
            finish();
            return;
          }
          const progress = Math.min(1, (now - started) / duration);
          const eased = 1 - Math.pow(1 - progress, 3);
          const startScale = 0.9;
          for (const view of transformed) {
            const scale = startScale + (1 - startScale) * eased;
            const startAlpha = reducedMotion ? 0.7 : 0.65 + effectScale * 0.2;
            view.container.alpha = startAlpha + (1 - startAlpha) * eased;
            view.container.scale.set(scale);
            view.container.x = view.baseX + ((1 - scale) * TILE_SIZE) / 2;
            view.container.y = view.baseY + ((1 - scale) * TILE_SIZE) / 2;
          }
          for (const pulse of pulses) pulse.alpha = 1 - progress;
          for (const fragment of fragments) {
            fragment.view.x = fragment.x + fragment.vx * eased;
            fragment.view.y = fragment.y + fragment.vy * eased + 8 * progress * progress;
            fragment.view.alpha = (1 - progress) * effectScale;
            fragment.view.scale.set(1 - progress * 0.45);
          }
          if (progress < 1) nextFrame(step);
          else finish();
        };
        nextFrame(step);
      });

    const fallingOffsets = (matched: number[]) => {
      const offsets = new Array<number>(BOARD_SIZE * BOARD_SIZE).fill(0);
      const removed = new Set(matched);
      const applySegment = (col: number, startRow: number, endRow: number) => {
        if (startRow > endRow) return;
        const survivors: number[] = [];
        for (let row = startRow; row <= endRow; row++) {
          if (!removed.has(row * BOARD_SIZE + col)) survivors.push(row);
        }
        const newCount = endRow - startRow + 1 - survivors.length;
        for (let row = startRow; row < startRow + newCount; row++)
          offsets[row * BOARD_SIZE + col] = Math.max(1, newCount);
        survivors.forEach((sourceRow, index) => {
          const finalRow = startRow + newCount + index;
          offsets[finalRow * BOARD_SIZE + col] = Math.max(0, finalRow - sourceRow);
        });
      };
      const locked = lockedRowRef.current;
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (locked >= 0 && locked < BOARD_SIZE) {
          applySegment(col, 0, locked - 1);
          applySegment(col, locked + 1, BOARD_SIZE - 1);
        } else {
          applySegment(col, 0, BOARD_SIZE - 1);
        }
      }
      return offsets;
    };

    const renderFallingBoard = (next: number[], matched: number[], currentGeneration: number) =>
      new Promise<void>((resolve) => {
        const offsets = fallingOffsets(matched);
        clearDragClones();
        drag = null;
        const moving: Array<{ view: TileView; distance: number; index: number }> = [];
        for (let index = 0; index < BOARD_SIZE * BOARD_SIZE; index++) {
          const distance = offsets[index];
          if (distance <= 0 && displayedBoard[index] === next[index]) continue;
          const previous = tileViews[index];
          if (previous) {
            if (previous.container.parent === boardLayer)
              boardLayer.removeChild(previous.container);
            previous.container.destroy({ children: true });
          }
          const container = createTile(next[index]);
          const view = {
            container,
            tile: next[index],
            baseX: (index % BOARD_SIZE) * TILE_SIZE,
            baseY: Math.floor(index / BOARD_SIZE) * TILE_SIZE,
          };
          container.x = view.baseX;
          container.y = view.baseY - distance * TILE_SIZE;
          boardLayer.addChild(container);
          tileViews[index] = view;
          if (distance > 0) moving.push({ view, distance, index });
        }
        displayedBoard = [...next];
        if (reducedMotion) {
          for (const { view } of moving) view.container.y = view.baseY;
          resolve();
          return;
        }
        if (moving.length === 0) {
          resolve();
          return;
        }
        const landingRows = new Map<number, number>();
        for (const { index } of moving) {
          const col = index % BOARD_SIZE;
          const row = Math.floor(index / BOARD_SIZE);
          landingRows.set(col, Math.max(row, landingRows.get(col) ?? -1));
        }
        const started = performance.now();
        let impacted = false;
        const step = (now: number) => {
          if (
            !animationActive(currentGeneration) ||
            moving.some(
              ({ view }) => view.container.destroyed || view.container.parent !== boardLayer,
            )
          ) {
            resolve();
            return;
          }
          const elapsed = now - started;
          if (elapsed < FALL_DROP_MS) {
            const progress = Math.min(1, elapsed / FALL_DROP_MS);
            const eased = progress * progress;
            for (const { view, distance } of moving) {
              const startY = view.baseY - distance * TILE_SIZE;
              view.container.y = startY + (view.baseY + 2 - startY) * eased;
            }
            nextFrame(step);
            return;
          }
          if (!impacted) {
            impacted = true;
            playLandingImpact(matched, next, landingRows, currentGeneration);
          }
          const settle = Math.min(1, (elapsed - FALL_DROP_MS) / FALL_SETTLE_MS);
          const squash = (0.04 + currentEffectScale("motion") * 0.06) * (1 - settle);
          for (const { view } of moving) {
            view.container.scale.set(1 + squash * 0.65, 1 - squash);
            view.container.x = view.baseX - (TILE_SIZE * squash * 0.65) / 2;
            view.container.y = view.baseY + TILE_SIZE * squash + 2 * (1 - settle);
          }
          if (settle < 1) {
            nextFrame(step);
            return;
          }
          for (const { view } of moving) {
            view.container.scale.set(1);
            view.container.x = view.baseX;
            view.container.y = view.baseY;
          }
          resolve();
        };
        nextFrame(step);
      });

    const playCascades = (
      steps: Cascade[],
      finalBoard: number[],
      initialTransformations: TileTransformation[],
    ) => {
      const currentGeneration = animationGeneration.begin();
      animating = true;
      host.dataset.effect = steps.length > 0 ? "cascade" : "transformation";
      host.dataset.effectCount = String(Number(host.dataset.effectCount ?? "0") + 1);
      clearMotionEffects();
      clearDragClones();
      drag = null;
      void (async () => {
        await animateTransformations(initialTransformations, currentGeneration);
        if (!animationActive(currentGeneration)) return;
        let source = [...displayedBoard];
        for (const cascade of steps) {
          if (!animationActive(currentGeneration)) return;
          emitCombatFlights(cascade);
          flashMatches(cascade, source, currentGeneration);
          await wait(reducedMotion ? 30 : FLASH_MS);
          if (!animationActive(currentGeneration)) return;
          if (!reducedMotion && fallDelayRef.current > 0) await wait(fallDelayRef.current);
          if (!animationActive(currentGeneration)) return;
          const settledBoard = [...cascade.board];
          for (const change of cascade.transformations ?? []) {
            if (change.index >= 0 && change.index < settledBoard.length)
              settledBoard[change.index] = change.from;
          }
          await renderFallingBoard(settledBoard, cascade.matched ?? [], currentGeneration);
          await animateTransformations(cascade.transformations ?? [], currentGeneration);
          source = [...cascade.board];
        }
        if (!animationActive(currentGeneration)) return;
        effectLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
        if (!boardsEqual(displayedBoard, finalBoard)) replaceBoard(finalBoard);
        animating = false;
        delete host.dataset.effect;
      })();
    };

    const applyAuthority = (next: DeferredAuthority) => {
      if (next.cascades.length > 0 || next.transformations.length > 0)
        playCascades(next.cascades, next.board, next.transformations);
      else renderAuthoritative(next.board);
    };

    flushAuthorityRef.current = () => {
      if (draggingRef.current) return;
      const next = deferredAuthorityRef.current;
      if (!next) return;
      deferredAuthorityRef.current = null;
      applyAuthority(next);
    };

    void Promise.all([
      app.init({
        width: BOARD_PIXELS,
        height: BOARD_PIXELS,
        antialias: false,
        backgroundColor: 0x111514,
        resolution: 1,
      }),
      Assets.load<Spritesheet>(CORE_ATLAS_JSON),
    ]).then(([, sheet]) => {
      if (disposed) {
        app.destroy(true);
        return;
      }
      coreSheet = sheet;
      initialized = true;
      app.canvas.className = "board-canvas";
      app.canvas.style.imageRendering = "pixelated";
      host.replaceChildren(app.canvas);
      app.stage.addChild(boardLayer, lockLayer, fogLayer, motionLayer, effectLayer);
      renderRef.current = renderAuthoritative;
      redrawLockRef.current = renderLock;
      redrawFogRef.current = renderFog;
      cascadeRef.current = playCascades;
      replaceBoard(boardRef.current);
      renderLock();
      renderFog();

      const position = (event: PointerEvent) => {
        const rect = app.canvas.getBoundingClientRect();
        return {
          x: ((event.clientX - rect.left) / rect.width) * BOARD_PIXELS,
          y: ((event.clientY - rect.top) / rect.height) * BOARD_PIXELS,
        };
      };

      app.canvas.addEventListener("pointerdown", (event) => {
        if (disabledRef.current || drag) return;
        if (animating) renderAuthoritative(boardRef.current);
        const point = position(event);
        const now = performance.now();
        draggingRef.current = true;
        drag = {
          id: event.pointerId,
          boardHash: boardHashRef.current,
          startX: point.x,
          startY: point.y,
          axis: null,
          index: 0,
          offset: 0,
          lastX: point.x,
          lastY: point.y,
          lastMoveAt: now,
          lastSparkAt: 0,
          views: [],
          clones: [],
        };
        app.canvas.setPointerCapture(event.pointerId);
      });
      app.canvas.addEventListener("pointermove", (event) => {
        if (!drag || drag.id !== event.pointerId || animating) return;
        const point = position(event);
        const dx = point.x - drag.startX;
        const dy = point.y - drag.startY;
        if (!drag.axis && Math.max(Math.abs(dx), Math.abs(dy)) >= 3) {
          drag.axis = Math.abs(dx) >= Math.abs(dy) ? "row" : "col";
          host.dataset.dragging = drag.axis;
          drag.index =
            drag.axis === "row"
              ? Math.floor(drag.startY / TILE_SIZE)
              : Math.floor(drag.startX / TILE_SIZE);
          prepareDragLine(drag);
        }
        if (drag.axis) {
          const offset = Math.max(
            -TILE_SIZE * 6,
            Math.min(TILE_SIZE * 6, drag.axis === "row" ? dx : dy),
          );
          applyDragOffset(drag, offset);
          const now = performance.now();
          const delta = drag.axis === "row" ? point.x - drag.lastX : point.y - drag.lastY;
          const speed = Math.abs(delta) / Math.max(1, now - drag.lastMoveAt);
          emitFrictionSparks(drag, point, delta, speed, now);
          drag.lastX = point.x;
          drag.lastY = point.y;
          drag.lastMoveAt = now;
        }
      });
      app.canvas.addEventListener("pointerup", (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        const state = drag;
        const finishDrag = (flush = true) => {
          draggingRef.current = false;
          if (flush) flushAuthorityRef.current?.();
        };
        if (disabledRef.current || !state.axis || Math.abs(state.offset) < TILE_SIZE * 0.42) {
          animateOffset(state, 0, () => {
            renderBoard(boardRef.current);
            animating = false;
            delete host.dataset.dragging;
            delete host.dataset.dragOffset;
            finishDrag();
          });
          return;
        }
        animating = true;
        const distance = Math.max(-6, Math.min(6, Math.round(state.offset / TILE_SIZE)));
        const sourceBoard =
          state.boardHash === boardHashRef.current ? displayedBoard : [...boardRef.current];
        const matches = wouldSlideMatch(
          sourceBoard,
          state.axis,
          state.index,
          distance,
          matchRulesRef.current,
        );
        if (matches === false) {
          host.dataset.rejectedSlide = "no-match";
          animateOffset(state, 0, () => {
            renderBoard(boardRef.current);
            animating = false;
            delete host.dataset.dragging;
            delete host.dataset.dragOffset;
            finishDrag();
          });
          return;
        }
        delete host.dataset.rejectedSlide;
        const submittedHash = boardHashRef.current;
        animateOffset(state, distance * TILE_SIZE, () => {
          const predicted = slideBoard(
            sourceBoard,
            state.axis as "row" | "col",
            state.index,
            distance,
          );
          renderBoard(predicted);
          deferredAuthorityRef.current = null;
          delete host.dataset.dragging;
          delete host.dataset.dragOffset;
          finishDrag(false);
          void slideRef.current(state.axis as "row" | "col", state.index, distance).finally(() => {
            schedule(() => {
              if (boardHashRef.current === submittedHash) {
                renderAuthoritative(boardRef.current);
                animating = false;
              }
            }, 100);
          });
        });
      });
      app.canvas.addEventListener("pointercancel", () => {
        if (!drag) return;
        const state = drag;
        animateOffset(state, 0, () => {
          renderBoard(boardRef.current);
          animating = false;
          delete host.dataset.dragging;
          delete host.dataset.dragOffset;
          draggingRef.current = false;
          flushAuthorityRef.current?.();
        });
      });
    });

    return () => {
      disposed = true;
      animationGeneration.invalidate();
      renderRef.current = null;
      redrawLockRef.current = null;
      redrawFogRef.current = null;
      cascadeRef.current = null;
      flushAuthorityRef.current = null;
      automatedSlideRef.current = null;
      automatedAnimationResolve?.(false);
      automatedAnimationResolve = null;
      draggingRef.current = false;
      deferredAuthorityRef.current = null;
      frameIDs.forEach((id) => window.cancelAnimationFrame(id));
      timeoutIDs.forEach((id) => window.clearTimeout(id));
      domAnimations.forEach((animation) => animation.cancel());
      domAnimations.clear();
      flightLayer.remove();
      if (initialized) app.destroy(true, { children: true });
    };
  }, []);

  useEffect(() => {
    if (boardHash === lastAnimatedHashRef.current) return;
    lastAnimatedHashRef.current = boardHash;
    const next = {
      board: [...board],
      boardHash,
      cascades: [...cascades],
      transformations: [...transformations],
    };
    if (draggingRef.current) {
      deferredAuthorityRef.current = next;
      return;
    }
    if (next.cascades.length > 0 || next.transformations.length > 0)
      cascadeRef.current?.(next.cascades, next.board, next.transformations);
    else renderRef.current?.(next.board);
  }, [board, boardHash, cascades, transformations, revision]);

  useEffect(() => {
    if (draggingRef.current) return;
    renderRef.current?.(boardRef.current);
  }, [colorBlind]);

  useEffect(() => {
    redrawLockRef.current?.();
  }, [lockedRow]);

  useEffect(() => {
    redrawFogRef.current?.();
  }, [foggedCells]);

  const effectIntensity = Math.round(
    Math.max(
      ...Object.values(effectSettings).map((setting) => (setting.enabled ? setting.intensity : 0)),
    ),
  );
  return (
    <div
      className={`board-host${disabled ? " is-disabled" : ""}${queued ? " is-queued" : ""}`}
      data-input-state={queued ? "queued" : disabled ? "blocked" : "ready"}
      data-effect-intensity={effectIntensity}
      ref={hostRef}
      aria-label="7乘7三消棋盘"
    />
  );
});

function slideBoard(
  board: number[],
  axis: "row" | "col",
  index: number,
  distance: number,
): number[] {
  const next = [...board];
  if (axis === "row") {
    for (let col = 0; col < BOARD_SIZE; col++) {
      next[index * BOARD_SIZE + mod(col + distance, BOARD_SIZE)] = board[index * BOARD_SIZE + col];
    }
  } else {
    for (let row = 0; row < BOARD_SIZE; row++) {
      next[mod(row + distance, BOARD_SIZE) * BOARD_SIZE + index] = board[row * BOARD_SIZE + index];
    }
  }
  return next;
}

function mod(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

function boardsEqual(left: number[], right: number[]): boolean {
  return left.length === right.length && left.every((tile, index) => tile === right[index]);
}
